/**
 * TanStack Query keys — the single registry (CLAUDE.md conventions). Feature code builds keys
 * here so invalidation stays consistent, e.g. `queryClient.invalidateQueries({ queryKey: qk.health })`.
 */
export const queryKeys = {
  health: ['health'] as const,
  me: ['auth', 'me'] as const,
  /** Public sign-in page config and password-link checks (T-13A, FR-AUTH-12, FR-AUTH-17). */
  auth: {
    config: ['auth', 'config'] as const,
    passwordToken: (token: string) => ['auth', 'password-token', token] as const,
  },
  /** `GET /dashboard` (FR-DASH): one response shared by every widget. */
  dashboard: ['dashboard'] as const,
  employees: {
    all: ['employees'] as const,
    /** One directory page; `params` = the request query (FR-EMP-5). */
    directory: (params: object) => ['employees', 'directory', params] as const,
    profile: (id: string) => ['employees', id, 'profile'] as const,
    /** History of one profile section (employment, salary, bank, contact, emergency). */
    section: (id: string, section: string) => ['employees', id, section] as const,
    /** The exit, status and direct reports of one employee (FR-EXIT-1, FR-MGR-4). */
    exit: (id: string) => ['employees', id, 'exit'] as const,
    /** My reporting tree (FR-MGR-3). */
    team: ['employees', 'team'] as const,
    /** Pay components and bank requirement for the salary dialog (FR-SAL-3/4). */
    salaryOptions: (id: string) => ['employees', id, 'salary-options'] as const,
    /** Suggested code, pay components and bank settings for the create form (FR-EMP-2). */
    createOptions: ['employees', 'create-options'] as const,
    /** Is this login email free (FR-EMP-4)? */
    emailAvailability: (email: string) => ['employees', 'email-availability', email] as const,
  },
  /** The whole org chart (FR-MGR-5); invalidated with employee writes. */
  orgChart: ['employees', 'org-chart'] as const,
  /** Approval requests (ADR-017); invalidate `all` after any decision or request. */
  approvals: {
    all: ['approvals'] as const,
    list: (params: object) => ['approvals', 'list', params] as const,
    detail: (id: string) => ['approvals', 'detail', id] as const,
    pendingCount: ['approvals', 'pending-count'] as const,
  },
  /** My in-app notifications (FR-NOTIF-1/2); invalidate `all` after marking read. */
  notifications: {
    all: ['notifications'] as const,
    list: (params: object) => ['notifications', 'list', params] as const,
  },
  /** Payroll set-up (FR-PAY-2/3/11): approver chain and standing Food / AIT pages. */
  payroll: {
    all: ['payroll'] as const,
    approverChain: ['payroll', 'approver-chain'] as const,
    /** Bank upload settings (T-06A). */
    bankUpload: ['payroll', 'bank-upload'] as const,
    standing: (kind: string, params: object) => ['payroll', 'standing', kind, params] as const,
    /** Runs (FR-PAY-5…16); invalidate `runs` after any run change. */
    runs: ['payroll', 'runs'] as const,
    runList: (params: object) => ['payroll', 'runs', 'list', params] as const,
    run: (id: string) => ['payroll', 'runs', id] as const,
    runLines: (id: string, params: object) => ['payroll', 'runs', id, 'lines', params] as const,
    /** Under `runs`, so creating a run refreshes it. */
    settlementCandidates: ['payroll', 'runs', 'settlement-candidates'] as const,
    /** My payslips (FR-PAY-18). */
    myPayslips: (params: object) => ['payroll', 'my-payslips', params] as const,
  },
  /** `/hr/exits` pages (FR-EXIT-4). */
  exits: {
    all: ['exits'] as const,
    list: (params: object) => ['exits', params] as const,
  },
  /** Admin users & roles (FR-ROLE-5); invalidate `all` after any account change. */
  adminUsers: {
    all: ['admin', 'users'] as const,
    list: (params: object) => ['admin', 'users', params] as const,
    /** An employee's sign-in account for the profile action (FR-ROLE-7, T-13B). */
    employeeAccount: (employeeId: string) => ['admin', 'users', 'employee', employeeId] as const,
  },
  /** Admin audit log (FR-AUD-4): keyset pages per filter set, and the people for the filters. */
  audit: {
    all: ['admin', 'audit'] as const,
    list: (filters: object) => ['admin', 'audit', 'list', filters] as const,
    people: ['admin', 'audit', 'people'] as const,
  },
  /** System settings and the pay component catalog (SRS §7, FR-SAL-3). */
  settings: {
    all: ['admin', 'settings'] as const,
    payComponents: ['admin', 'settings', 'pay-components'] as const,
  },
  /** Departments (with designations) and banks (FR-LKP); invalidate `all` after any write. */
  lookups: {
    all: ['lookups'] as const,
    departments: (includeInactive: boolean) =>
      ['lookups', 'departments', { includeInactive }] as const,
    banks: (includeInactive: boolean) => ['lookups', 'banks', { includeInactive }] as const,
  },
  /** Leave policy, balances and requests (FR-LEAVE-1…8); invalidate `all` after any leave write. */
  leave: {
    types: (includeInactive: boolean) => ['leave', 'types', { includeInactive }] as const,
    all: ['leave'] as const,
    mine: (year: number) => ['leave', 'mine', year] as const,
    requests: (params: object) => ['leave', 'requests', params] as const,
    calendar: (params: object) => ['leave', 'calendar', params] as const,
    balances: (employeeId: string, year: number) =>
      ['leave', 'balances', employeeId, year] as const,
    byApproval: (approvalId: string) => ['leave', 'by-approval', approvalId] as const,
  },
  /** Attendance: WFH requests and calendar, offices (FR-WFH, FR-ATT-2); invalidate `all` after writes. */
  attendance: {
    all: ['attendance'] as const,
    wfh: (params: object) => ['attendance', 'wfh', params] as const,
    wfhCalendar: (params: object) => ['attendance', 'wfh-calendar', params] as const,
    offices: ['attendance', 'offices'] as const,
    today: ['attendance', 'today'] as const,
    devices: ['attendance', 'devices'] as const,
    unmapped: ['attendance', 'devices', 'unmapped'] as const,
    deviceMaps: (deviceId: string) => ['attendance', 'devices', deviceId, 'map'] as const,
    regularisations: (params: object) => ['attendance', 'regularisations', params] as const,
    flagged: (params: object) => ['attendance', 'flagged', params] as const,
    myMonth: (month: string) => ['attendance', 'my-month', month] as const,
    teamToday: ['attendance', 'team-today'] as const,
    todaySummary: ['attendance', 'today-summary'] as const,
    grid: (params: object) => ['attendance', 'grid', params] as const,
    day: (params: object) => ['attendance', 'day', params] as const,
  },
  /** Work calendar holidays per year (FR-CAL-2); invalidate `all` after any write. */
  holidays: {
    all: ['holidays'] as const,
    year: (year: number) => ['holidays', year] as const,
  },
} as const;
